import * as css from "./styles/acquisitionPropertyFormStyles.module.scss";

import {Field, Label, Toggle} from "@zendeskgarden/react-forms";
import {
    RevenueModelType,
    useListAcceptableUsersForAcquisitionPropertyLazyQuery,
    useListBudgetYearsLazyQuery,
    VersionType
} from "../../../../__generated__/generated_types";
import AcquisitionPropertyValidator, {TFormError} from "../../logic/acquisitionPropertyValidator";
import AcquisitionPropertyFormDropdown from './AcquisitionPropertyFormDropdown';
import AcquisitionPropertyFormInput from "./AcquisitionPropertyFormInput";
import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {Separator} from "@zendeskgarden/react-dropdowns";
import {Body, Footer, FooterItem, Header} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../analyst/ViziblyZDGTheme";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../constants/Colors";
import AcquisitionPropertyFormMultiselect from "./AcquisitionPropertyFormMultiselect";
import {getRevenueModelsForUser} from "../../../property/common/revenue-modal-options";
import {getRevenueModelDisplayName} from "../../../../constants/Strings";
import {AuthContext} from "../../../../contexts/AuthContext";


export type TAcquisitionPropertyRecord = {
    id?: string;
    name: string;
    isDeactivated: boolean;
    acquiredPropertyId?: string;
    acquisitionBudgetStartMonth: number;
    acquisitionBudgetYear: number;
    acquisitionBudgetPeriod: VersionType.Budget | VersionType.Reforecast;
    reforecastYear: number;
    revenueConfiguration: RevenueModelType;
    originalRevenueModelSubsidyEnabled: boolean;
}

export interface IAcquisitionPropertyForm {
    isCreate?: boolean,
    property: TAcquisitionPropertyRecord,
    loading: boolean,
    usersForProperty: string[],
    onClose: () => void,
    onChange: (updates: Partial<TAcquisitionPropertyRecord>) => void,
    onUsersChange: (users: string[]) => void,
    onSubmit: () => void,
}

export default function AcquisitionPropertyForm(props: IAcquisitionPropertyForm): ReactElement {
    const {
        isCreate = false,
        property,
        loading,
        usersForProperty,
        onClose,
        onChange,
        onUsersChange,
        onSubmit,
    } = props;

    const {user} = useContext(AuthContext);

    const propertyValidator = new AcquisitionPropertyValidator();
    const [formErrors, setFormErrors] = useState<TFormError[]>([]);
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const [getUsers, { loading: usersLoading, data: usersData }] = useListAcceptableUsersForAcquisitionPropertyLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [getBudgetYears, {data: budgetYearsListData}] = useListBudgetYearsLazyQuery({
        fetchPolicy: "no-cache"
    });

    function checkValidOnBlur(field: string): void {
        if (formErrors.find(x => x.field == field)) {
            setFormErrors(propertyValidator.validate(property));
        }
    }

    function submit(): void {
        const errors = propertyValidator.validate(property);

        if (errors.length == 0) {
            onSubmit();
        } else {
            setFormErrors(errors);
        }
    }

    useEffect(() => {
        getBudgetYears();
        getUsers();
    }, []);

    const revenueModelOptions = useMemo(() => getRevenueModelsForUser(user), [user]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Header>{isCreate ? "Add" : "Edit"} Budget Builder Property</Header>
            <Body>
                <form className={css.acquisitionPropertyForm}>
                    <AcquisitionPropertyFormInput
                        label={"Property Name"}
                        placeholder={"Sherwood Forest"}
                        required
                        value={property.name}
                        error={formErrors.find(x => x.field == "name")?.message}
                        onChange={evt => onChange({ name: evt.target.value })}
                        onBlur={() => checkValidOnBlur('name')}
                    />

                    {!isCreate &&
                        <Field>
                            <Toggle checked={property.isDeactivated}
                                    onChange={evt => onChange({ isDeactivated: evt.target.checked })}>
                                <Label>Is Deactivated?</Label>
                            </Toggle>
                        </Field>
                    }

                    {budgetYearsListData && isCreate &&
                        <AcquisitionPropertyFormDropdown
                            label={"Budget Season Chart of Accounts to Use"}
                            required
                            selection={`${property.reforecastYear}-${property.reforecastYear + 1}`}
                            options={budgetYearsListData.listBudgetYears.budgetYears.map(x => `${ x.year - 1 }-${ x.year }`).sort()}
                            error={formErrors.find(x => x.field == "reforecastYear")?.message}
                            onSelect={evt => {
                                const reforecastYear = parseInt(evt.split("-")[0]);
                                onChange({
                                    reforecastYear,
                                });
                            }}
                            onBlur={() => checkValidOnBlur('reforecastYear')}
                        />
                    }

                    <div className={css.sideBySideFields}>
                        <AcquisitionPropertyFormDropdown
                            label={"Budget Start Month"}
                            required
                            selection={months[property.acquisitionBudgetStartMonth] || ''}
                            options={months.map((month, index) => ({ name: month, value: index }))}
                            error={formErrors.find(x => x.field == "acquisitionBudgetStartMonth")?.message}
                            onSelect={item => onChange({ acquisitionBudgetStartMonth: item.value })}
                            onBlur={() => checkValidOnBlur('acquisitionBudgetStartMonth')}
                        />

                        <AcquisitionPropertyFormInput
                            label={"Budget Year"}
                            placeholder={"2025"}
                            required
                            value={property.acquisitionBudgetYear}
                            error={formErrors.find(x => x.field == "acquisitionBudgetYear")?.message}
                            onChange={evt => onChange({ acquisitionBudgetYear: parseInt(evt.target.value) })}
                            onBlur={() => checkValidOnBlur('acquisitionBudgetYear')}
                        />
                    </div>
                    <AcquisitionPropertyFormDropdown
                        label="Revenue Model"
                        required
                        selection={{name: getRevenueModelDisplayName(property.revenueConfiguration), value: property.revenueConfiguration}}
                        error={formErrors.find(x => x.field == "revenueConfiguration")?.message}
                        onSelect={item => onChange({ revenueConfiguration: item.value })}
                        onBlur={() => checkValidOnBlur('revenueConfiguration')}
                        options={revenueModelOptions.map(x => ({name: x.label, value: x.value}))}
                    />

                    {(property.revenueConfiguration === RevenueModelType.Original || property.revenueConfiguration === RevenueModelType.LeaseUp) &&
                        <Field>
                            <Toggle checked={property.originalRevenueModelSubsidyEnabled}
                                    onChange={evt => onChange({ originalRevenueModelSubsidyEnabled: evt.target.checked })}>
                                    <Label>Enable Rent Subsidy?</Label>
                            </Toggle>
                        </Field>
                    }

                    {
                        !usersLoading && usersData &&
                            <AcquisitionPropertyFormMultiselect
                                label={"User Access"}
                                selection={usersForProperty || []}
                                options={usersData.listAcceptableUsersForAcquisitionProperty.sortBy(x => x.lastName).map(x => ({name: `${x.firstName} ${x.lastName}`, value: x.id}))}
                                onSelect={onUsersChange}
                            />
                    }
                </form>
            </Body>
            <Separator />
            <Footer>
                <FooterItem>
                    <Button onClick={onClose} isBasic disabled={loading}>
                        Cancel
                    </Button>
                </FooterItem>
                <FooterItem>
                    <Button isPrimary onClick={submit} disabled={loading}>
                        {
                            loading
                                ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                : <span>Save</span>
                        }
                    </Button>
                </FooterItem>
            </Footer>
        </ThemeProvider>
    );
}